import {
  Box,
  SimpleGrid,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Icon,
  useColorModeValue,
  Badge,
  Spinner,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiShield,
  FiTrendingUp,
  FiPackage,
  FiArrowDown,
  FiHexagon,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { PageHero } from "../components/common/PageHero";
import { SectionWrapper } from "../components/common/SectionWrapper";
import { useFunds } from "../hooks/useFunds";
import {
  FUND_CATEGORY_CONFIGS,
  filterFundsByCategory,
} from "../config/fundCategories";

const MotionBox = motion(Box);

export const FundsPage = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useFunds();
  const cardBg = useColorModeValue("white", "whiteAlpha.50");
  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const funds = data?.items || [];

  const scrollToCategories = () => {
    const el = document.getElementById("funds-categories");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box dir="rtl" overflowX="hidden">
      <Navbar />
      <PageHero
        title="صندوق‌ها و محصولات سرمایه‌گذاری"
        titleHighlight="تأمین سرمایه امید"
        subtitle="با انتخاب از میان صندوق‌های متنوع ما، سرمایه خود را هوشمندانه رشد دهید و از فرصت‌های بی‌نظیر بازار بهره‌مند شوید"
        primaryAction={{
          label: "مشاهده صندوق‌ها",
          icon: FiArrowDown,
          onClick: scrollToCategories,
        }}
      />

      {/* Category Cards */}
      <SectionWrapper id="funds-categories" contentMaxW="container.xl">
        <VStack spacing={4} textAlign="center" mb={10}>
          <Heading
            as="h2"
            fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
            color={textColor}
          >
            دسته‌بندی صندوق‌ها
          </Heading>
          <Text
            fontSize={{ base: "sm", md: "md" }}
            color={subtitleColor}
            maxW="600px"
          >
            نوع صندوق مورد نظر خود را انتخاب کنید
          </Text>
        </VStack>

        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          spacing={{ base: 6, md: 8 }}
          w="100%"
        >
          {FUND_CATEGORY_CONFIGS.map((cat, index) => {
            const fundCount = filterFundsByCategory(funds, cat.key).length;
            const iconByCategory = {
              "fixed-income": FiShield,
              equity: FiTrendingUp,
              "market-making": FiPackage,
              commodity: FiHexagon,
            } as const;
            const IconComponent = iconByCategory[cat.key];

            return (
              <MotionBox
                key={cat.path}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  p={8}
                  border="1px solid"
                  borderColor={borderColor}
                  shadow="sm"
                  _hover={{
                    shadow: "xl",
                    transform: "translateY(-6px)",
                    borderColor: `${cat.colorScheme}.300`,
                  }}
                  transition="all 0.3s ease"
                  cursor="pointer"
                  onClick={() => navigate(cat.path)}
                  h="100%"
                  display="flex"
                  flexDirection="column"
                >
                  <VStack spacing={4} align="start" flex={1}>
                    <Box
                      bg={`${cat.colorScheme}.100`}
                      _dark={{ bg: `${cat.colorScheme}.200` }}
                      p={3}
                      borderRadius="xl"
                    >
                      <Icon
                        verticalAlign="middle"
                        as={IconComponent as any}
                        boxSize={7}
                        color={`${cat.colorScheme}.500`}
                      />
                    </Box>

                    <HStack spacing={3}>
                      <Heading
                        as="h3"
                        fontSize={{ base: "lg", md: "xl" }}
                        color={textColor}
                      >
                        {cat.title}
                      </Heading>
                      {fundCount > 0 && (
                        <Badge
                          colorScheme={cat.colorScheme}
                          fontSize="sm"
                          px={2}
                          py={0.5}
                          borderRadius="full"
                        >
                          {fundCount} صندوق
                        </Badge>
                      )}
                    </HStack>

                    <Text
                      fontSize="sm"
                      color={subtitleColor}
                      lineHeight="tall"
                      flex={1}
                    >
                      {cat.description}
                    </Text>

                    <Button
                      variant="outline"
                      colorScheme={cat.colorScheme}
                      rightIcon={
                        <Icon verticalAlign="middle" as={FiArrowLeft as any} />
                      }
                      size="md"
                      mt={2}
                      _hover={{
                        transform: "translateX(-4px)",
                      }}
                      transition="all 0.3s"
                    >
                      مشاهده
                    </Button>
                  </VStack>
                </Box>
              </MotionBox>
            );
          })}
        </SimpleGrid>
      </SectionWrapper>

      {/* Quick Stats Section */}
      <SectionWrapper alt contentMaxW="container.xl">
        <VStack spacing={4} textAlign="center" mb={10}>
          <Heading
            as="h2"
            fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
            color={textColor}
          >
            آمار صندوق‌های ما
          </Heading>
        </VStack>

        {isLoading ? (
          <HStack justify="center" py={8}>
            <Spinner color="brand.500" />
            <Text color={subtitleColor}>در حال دریافت اطلاعات صندوق‌ها...</Text>
          </HStack>
        ) : (
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
            {[
              {
                label: "تعداد صندوق‌ها",
                value: funds.length.toString(),
                suffix: "صندوق",
              },
              {
                label: "مجموع سرمایه‌گذاران",
                value: new Intl.NumberFormat("fa-IR").format(
                  funds.reduce((sum, f) => sum + f.nav.totalInvestor, 0),
                ),
                suffix: "نفر",
              },
              {
                label: "مجموع ارزش خالص دارایی",
                value: `${(
                  funds.reduce((sum, f) => sum + f.nav.totalNetAssetValue, 0) /
                  1_000_000_000_000
                ).toFixed(1)}`,
                suffix: "هزار میلیارد ریال",
              },
              {
                label: "بالاترین بازدهی سالانه",
                value:
                  funds.length > 0
                    ? `${Math.max(...funds.map((f) => f.annualReturn)).toFixed(1)}%`
                    : "0%",
                suffix: "",
              },
            ].map((stat, i) => (
              <MotionBox
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <Box
                  bg={cardBg}
                  borderRadius="xl"
                  p={6}
                  textAlign="center"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <Text
                    fontSize={{ base: "2xl", md: "3xl" }}
                    fontWeight="extrabold"
                    color="brand.500"
                  >
                    {stat.value}
                  </Text>
                  <Text fontSize="xs" color={subtitleColor} mt={1}>
                    {stat.suffix}
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={textColor}
                    mt={2}
                  >
                    {stat.label}
                  </Text>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        )}
      </SectionWrapper>

      <Footer />
    </Box>
  );
};
